import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  Spinner,
  Alert,
  Badge,
  ListGroup,
  Card,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  get_all_shipments,
  get_pending_shipments,
  add_manual_tracking_update,
  change_shipment_type,
} from "../reduxData/user/userAction";

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "REQUESTED":
      return "warning";
    case "PENDING_SHIPMENT":
      return "info";
    case "SHIPPED":
      return "primary";
    case "TRANSIT":
      return "primary";
    case "DELIVERED":
      return "success";
    case "COMPLETED":
      return "success";
    case "ERROR":
      return "danger";
    case "DISPUTED":
      return "danger";
    default:
      return "secondary";
  }
};

const CreateShipmentModal = ({ show, onHide, pendingShipments, onCreate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredShipments = useMemo(() => {
    if (!searchTerm) return pendingShipments;
    const lowercasedFilter = searchTerm.toLowerCase();
    return pendingShipments.filter(
      (shipment) =>
        shipment.item_id?.item_name?.toLowerCase().includes(lowercasedFilter) ||
        shipment.buyer_id?.user_name
          ?.toLowerCase()
          .includes(lowercasedFilter) ||
        shipment.seller_id?.user_name?.toLowerCase().includes(lowercasedFilter)
    );
  }, [searchTerm, pendingShipments]);

  const handleCreate = async () => {
    if (!selectedShipment) return;
    setIsSubmitting(true);
    try {
      await onCreate(selectedShipment._id, "insertbid");
      handleClose();
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedShipment(null);
    setSearchTerm("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create InsertBid Shipment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">
          Select a shipment to convert to InsertBid's manual tracking service.
        </p>
        <InputGroup className="mb-3">
          <InputGroup.Text>🔍</InputGroup.Text>
          <Form.Control
            placeholder="Search by item name, buyer, or seller..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          <Table hover>
            <thead>
              <tr>
                <th>Image</th>
                <th>Item Name</th>
                <th>Buyer</th>
                <th>Seller</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredShipments.length > 0 ? (
                filteredShipments.map((shipment) => (
                  <tr
                    key={shipment._id}
                    className={
                      selectedShipment?._id === shipment._id
                        ? "table-active"
                        : ""
                    }
                  >
                    <td>
                      <img
                        src={
                          shipment.item_id?.photos?.[0] ||
                          "https://via.placeholder.com/60"
                        }
                        alt={shipment.item_id?.item_name}
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </td>
                    <td className="align-middle">
                      {shipment.item_id?.item_name}
                    </td>
                    <td className="align-middle">
                      {shipment.buyer_id?.user_name}
                    </td>
                    <td className="align-middle">
                      {shipment.seller_id?.user_name}
                    </td>
                    <td className="align-middle">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setSelectedShipment(shipment)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    No pending shipments found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="btn-custom-primary"
          onClick={handleCreate}
          disabled={!selectedShipment || isSubmitting}
        >
          {isSubmitting ? <Spinner size="sm" /> : "Create Shipment"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditShipmentModal = ({ show, onHide, shipment, onUpdate }) => {
  const [statusDetails, setStatusDetails] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!shipment) return null;

  const handleUpdate = async () => {
    setIsSubmitting(true);
    try {
      await onUpdate(shipment._id, {
        status_details: statusDetails,
        city,
        state,
        new_status: newStatus,
      });
      setStatusDetails("");
      setCity("");
      setState("");
      setNewStatus("");
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAddressBlock = (title, details) => {
    if (!details) return null;
    return (
      <div className="col-md-6 mb-3">
        <Card className="h-100">
          <Card.Header className="fw-bold">{title}</Card.Header>
          <Card.Body>
            <p className="mb-1">
              <strong>Name:</strong> {details.name}
            </p>
            <p className="mb-1">
              <strong>Email:</strong> {details.email}
            </p>
            <p className="mb-1">
              <strong>Phone:</strong> {details.phone}
            </p>
            <hr className="my-2" />
            <p className="mb-1">{details.address}</p>
            {details.addressLine2 && (
              <p className="mb-1">{details.addressLine2}</p>
            )}
            <p className="mb-1">
              {details.city}, {details.state} {details.zip}
            </p>
            <p className="mb-0">{details.country}</p>
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Edit Shipment: {shipment.item_id?.item_name || "Unknown Item"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5 className="mb-3">
          Current Status:{" "}
          <Badge bg={getStatusBadgeColor(shipment.status)}>
            {shipment.status}
          </Badge>
        </h5>

        {shipment.shipping_details && (
          <div className="row mb-4">
            {renderAddressBlock(
              "Pickup From",
              shipment.shipping_details.pickup
            )}
            {renderAddressBlock(
              "Deliver To",
              shipment.shipping_details.delivery
            )}
          </div>
        )}

        <Card className="mb-3">
          <Card.Header>Tracking History</Card.Header>
          <ListGroup variant="flush">
            {shipment.trackingHistory && shipment.trackingHistory.length > 0 ? (
              [...shipment.trackingHistory].reverse().map((event, i) => (
                <ListGroup.Item key={i}>
                  <strong>{event.status_details}</strong>
                  <small className="d-block text-muted">
                    {new Date(event.status_date).toLocaleString()} -{" "}
                    {event.location?.city}, {event.location?.state}
                  </small>
                </ListGroup.Item>
              ))
            ) : (
              <ListGroup.Item className="text-muted text-center">
                No tracking updates yet.
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>

        <Card bg="light">
          <Card.Header>Add Manual Update</Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Update Details</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Package picked up, In Transit..."
                  value={statusDetails}
                  onChange={(e) => setStatusDetails(e.target.value)}
                />
              </Form.Group>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Location City</Form.Label>
                    <Form.Control
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Location State</Form.Label>
                    <Form.Control
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Update Overall Status (Optional)</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="">-- No Change --</option>
                  <option value="PENDING_SHIPMENT">Pending Shipment</option>
                  <option value="SHIPPED">Shipped</option>
                  <option value="TRANSIT">In Transit</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="ERROR">Error</option>
                </Form.Select>
              </Form.Group>
              <Button
                variant="success"
                onClick={handleUpdate}
                disabled={isSubmitting || !statusDetails}
              >
                {isSubmitting ? <Spinner size="sm" /> : "Add Update"}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default function ShipmentManager() {
  const [shipments, setShipments] = useState([]);
  const [pendingShipments, setPendingShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [shipmentsData, pendingData] = await Promise.all([
        get_all_shipments(),
        get_pending_shipments(),
      ]);
      setShipments(shipmentsData);
      setPendingShipments(pendingData);
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditShipment = (shipment) => {
    setSelectedShipment(shipment);
    setShowEditModal(true);
  };

  const handleUpdateShipment = async (id, payload) => {
    const updatedShipment = await add_manual_tracking_update(id, payload);
    if (updatedShipment) {
      setShipments((prev) =>
        prev.map((s) => (s._id === id ? updatedShipment : s))
      );
      setSelectedShipment(updatedShipment);
    }
  };

  const handleCreateNewInsertBidShipment = async (id, type) => {
    await change_shipment_type(id, type);
    fetchData();
  };

  if (loading)
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>InsertBid Shipment Manager</h2>
        <Button
          className="btn-custom-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + Create New Shipment
        </Button>
      </div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Item</th>
            <th>Buyer</th>
            <th>Seller</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {shipments.map((shipment) => (
            <tr key={shipment._id}>
              <td>{shipment.item_id?.item_name || "N/A"}</td>
              <td
                className="cursor-pointer"
                onClick={() => {
                  if (shipment.buyer_id?._id) {
                    navigate(`/editUser/${shipment.buyer_id._id}`);
                  }
                }}
              >
                {shipment.buyer_id?.user_name || "N/A"}
              </td>

              <td
                className="cursor-pointer"
                onClick={() => {
                  if (shipment.seller_id?._id) {
                    navigate(`/editUser/${shipment.seller_id._id}`);
                  }
                }}
              >
                {shipment.seller_id?.user_name || "N/A"}
              </td>
              <td>
                <Badge bg={getStatusBadgeColor(shipment.status)}>
                  {shipment.status}
                </Badge>
              </td>
              <td>{new Date(shipment.createdAt).toLocaleDateString()}</td>
              <td>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleEditShipment(shipment)}
                >
                  View / Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <CreateShipmentModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        pendingShipments={pendingShipments}
        onCreate={handleCreateNewInsertBidShipment}
      />
      <EditShipmentModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        shipment={selectedShipment}
        onUpdate={handleUpdateShipment}
      />
    </div>
  );
}
